import { Choice, VEHICLE_FIELDS } from '../vehicles/types';

export interface User {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
  username: string;
  is_active: boolean;
  is_superuser: boolean;
  user_type: 'admin' | 'client' | 'warehouse';
  user_type_display: string;
  created: Date;
  updated: Date;
  id: string;
  permissions: Array<string>;
}

// User Type Constants
export const USER_TYPE_ADMIN = 'admin';
export const USER_TYPE_CLIENT = 'client';
export const USER_TYPE_WAREHOUSE = 'warehouse';
export const USER_TYPE_PUBLIC = 'public';

/* eslint-disable camelcase */
// User Type Options for UI components
export const USER_TYPE_OPTIONS: Choice[] = [
  { value: USER_TYPE_ADMIN, display_name: 'Admin' },
  { value: USER_TYPE_CLIENT, display_name: 'Client' },
  { value: USER_TYPE_WAREHOUSE, display_name: 'Warehouse' },
];
/* eslint-enable camelcase */

// Helper function to get display name from user type value
export const getUserTypeDisplayName = (userType: string): string => {
  const option = USER_TYPE_OPTIONS.find(opt => opt.value === userType);
  return option?.display_name || userType;
};

// Type for vehicle field names
type VehicleFieldName = (typeof VEHICLE_FIELDS)[keyof typeof VEHICLE_FIELDS];

// Fields visible to clients
const CLIENT_VISIBLE_FIELDS: VehicleFieldName[] = [
  VEHICLE_FIELDS.PICTURES,
  VEHICLE_FIELDS.VIN,
  VEHICLE_FIELDS.NAME,
  VEHICLE_FIELDS.FUEL_TYPE,
  VEHICLE_FIELDS.MECHANICAL_STATUS,
  VEHICLE_FIELDS.KEYS,
  VEHICLE_FIELDS.STATUS,
  VEHICLE_FIELDS.LOCATION,
  VEHICLE_FIELDS.STOCK,
  VEHICLE_FIELDS.LOADER,
  VEHICLE_FIELDS.DESTINATION,
  VEHICLE_FIELDS.CLIENT,
  VEHICLE_FIELDS.COMMENT,
  VEHICLE_FIELDS.IMPORTER,
  VEHICLE_FIELDS.NOTIFY,
  VEHICLE_FIELDS.WAREHOUSE_DELIVERY,
  VEHICLE_FIELDS.SHIPPING_LINE,
  VEHICLE_FIELDS.CONTAINER_NUMBER,
  VEHICLE_FIELDS.WEIGHT,
  VEHICLE_FIELDS.VENDOR,
  // Financial fields that clients can see
  VEHICLE_FIELDS.INVOICE_DATE,
  VEHICLE_FIELDS.SALE_AMOUNT,
  VEHICLE_FIELDS.FEES_AMOUNT,
  VEHICLE_FIELDS.GST_AMOUNT,
  VEHICLE_FIELDS.PST_AMOUNT,
  VEHICLE_FIELDS.TOTAL_AMOUNT,
  // Document-related fields
  VEHICLE_FIELDS.TITLE_STATUS,
];

// Fields editable by clients (when creating new vehicle or vehicle not confirmed)
const CLIENT_EDITABLE_FIELDS: VehicleFieldName[] = [
  VEHICLE_FIELDS.PICTURES,
  VEHICLE_FIELDS.VIN,
  VEHICLE_FIELDS.NAME,
  VEHICLE_FIELDS.FUEL_TYPE,
  VEHICLE_FIELDS.MECHANICAL_STATUS,
  VEHICLE_FIELDS.KEYS,
  VEHICLE_FIELDS.LOCATION,
  VEHICLE_FIELDS.STOCK,
  VEHICLE_FIELDS.LOADER,
  VEHICLE_FIELDS.DESTINATION,
  VEHICLE_FIELDS.CLIENT,
  VEHICLE_FIELDS.COMMENT,
  VEHICLE_FIELDS.IMPORTER,
  VEHICLE_FIELDS.NOTIFY,
  VEHICLE_FIELDS.WAREHOUSE_DELIVERY,
  VEHICLE_FIELDS.SHIPPING_LINE,
  VEHICLE_FIELDS.CONTAINER_NUMBER,
  VEHICLE_FIELDS.WEIGHT,
  VEHICLE_FIELDS.VENDOR,
  VEHICLE_FIELDS.TITLE_STATUS,
];

// Fields visible to warehouse users
const WAREHOUSE_VISIBLE_FIELDS: VehicleFieldName[] = [
  VEHICLE_FIELDS.PICTURES,
  VEHICLE_FIELDS.VIN,
  VEHICLE_FIELDS.NAME,
  VEHICLE_FIELDS.FUEL_TYPE,
  VEHICLE_FIELDS.MECHANICAL_STATUS,
  VEHICLE_FIELDS.KEYS,
  VEHICLE_FIELDS.STATUS,
  VEHICLE_FIELDS.LOCATION,
  VEHICLE_FIELDS.STOCK,
  VEHICLE_FIELDS.CARRIER,
  VEHICLE_FIELDS.LOADER,
  VEHICLE_FIELDS.DESTINATION,
  VEHICLE_FIELDS.PRIVATE_NOTES,
];

// Fields editable by warehouse users
const WAREHOUSE_EDITABLE_FIELDS: VehicleFieldName[] = [
  VEHICLE_FIELDS.PICTURES,
  VEHICLE_FIELDS.FUEL_TYPE,
  VEHICLE_FIELDS.MECHANICAL_STATUS,
  VEHICLE_FIELDS.KEYS,
  VEHICLE_FIELDS.PRIVATE_NOTES,
];

// Fields visible to public (unauthenticated) users
const PUBLIC_VISIBLE_FIELDS: VehicleFieldName[] = [
  VEHICLE_FIELDS.PICTURES,
  VEHICLE_FIELDS.VIN,
  VEHICLE_FIELDS.NAME,
  VEHICLE_FIELDS.FUEL_TYPE,
  VEHICLE_FIELDS.MECHANICAL_STATUS,
  VEHICLE_FIELDS.KEYS,
  VEHICLE_FIELDS.STATUS,
  VEHICLE_FIELDS.CLIENT,
  VEHICLE_FIELDS.VENDOR,
  VEHICLE_FIELDS.LOCATION,
  VEHICLE_FIELDS.STOCK,
  VEHICLE_FIELDS.LOADER,
  VEHICLE_FIELDS.DESTINATION,
  VEHICLE_FIELDS.NOTIFY,
  VEHICLE_FIELDS.SHIPPING_LINE,
  VEHICLE_FIELDS.CONTAINER_NUMBER,
];

// Permission Maps by User Type
const PERMISSION_MAPS = {
  [USER_TYPE_ADMIN]: {
    visible: Object.values(VEHICLE_FIELDS),
    editable: Object.values(VEHICLE_FIELDS),
  },
  [USER_TYPE_CLIENT]: {
    visible: CLIENT_VISIBLE_FIELDS,
    editable: CLIENT_EDITABLE_FIELDS,
  },
  [USER_TYPE_WAREHOUSE]: {
    visible: WAREHOUSE_VISIBLE_FIELDS,
    editable: WAREHOUSE_EDITABLE_FIELDS,
  },
  [USER_TYPE_PUBLIC]: {
    visible: PUBLIC_VISIBLE_FIELDS,
    editable: [] as VehicleFieldName[],
  },
};

// Section name constants
export const SECTION_FINANCIALS = 'financials' as const;
export const SECTION_DOCUMENTS = 'documents' as const;
export const SECTION_PUBLIC_LINK = 'public_link' as const;

// Section names type
export type SectionName = typeof SECTION_FINANCIALS | typeof SECTION_DOCUMENTS | typeof SECTION_PUBLIC_LINK;

// Section-level permission maps
const SECTION_PERMISSIONS = {
  [SECTION_FINANCIALS]: {
    [USER_TYPE_ADMIN]: true,
    [USER_TYPE_CLIENT]: true,
    [USER_TYPE_WAREHOUSE]: false,
    [USER_TYPE_PUBLIC]: false,
  },
  [SECTION_DOCUMENTS]: {
    [USER_TYPE_ADMIN]: true,
    [USER_TYPE_CLIENT]: true,
    [USER_TYPE_WAREHOUSE]: false,
    [USER_TYPE_PUBLIC]: false,
  },
  [SECTION_PUBLIC_LINK]: {
    [USER_TYPE_ADMIN]: true,
    [USER_TYPE_CLIENT]: true,
    [USER_TYPE_WAREHOUSE]: false,
    [USER_TYPE_PUBLIC]: false,
  },
};

// Permission helper functions
export const canViewField = (userType: string, fieldName: string): boolean => {
  const permissions = PERMISSION_MAPS[userType as keyof typeof PERMISSION_MAPS];
  if (!permissions) return false;
  return permissions.visible.includes(fieldName as VehicleFieldName);
};

export const canEditField = (
  userType: string,
  fieldName: string,
  isNewVehicle = false,
  isVehicleNotConfirmed = false
): boolean => {
  const permissions = PERMISSION_MAPS[userType as keyof typeof PERMISSION_MAPS];
  if (!permissions) return false;

  // Admin can always edit
  if (userType === USER_TYPE_ADMIN) {
    return permissions.editable.includes(fieldName as VehicleFieldName);
  }

  // Warehouse users can edit any vehicle they can see (no status restrictions)
  if (userType === USER_TYPE_WAREHOUSE) {
    return permissions.editable.includes(fieldName as VehicleFieldName);
  }

  // Client users can edit only when creating new vehicle or vehicle not confirmed
  // Exception: Clients can always add pictures to any vehicle
  if (userType === USER_TYPE_CLIENT) {
    if (fieldName === VEHICLE_FIELDS.PICTURES) {
      return permissions.editable.includes(fieldName as VehicleFieldName);
    }
    const canEditBasedOnState = isNewVehicle || isVehicleNotConfirmed;
    return canEditBasedOnState && permissions.editable.includes(fieldName as VehicleFieldName);
  }

  return false;
};

// Section-level permission helper function
export const canViewSection = (userType: string, sectionName: SectionName): boolean => {
  const sectionPermissions = SECTION_PERMISSIONS[sectionName];
  if (!sectionPermissions) return false;
  return sectionPermissions[userType as keyof typeof sectionPermissions] || false;
};
